import React, { useEffect, useMemo, useState } from 'react';

function dataHoraBR(valor) {
  if (!valor) return '—';
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? '—'
    : data.toLocaleString('pt-BR');
}

function nomeRemetente(email) {
  return email.remetente_nome || email.remetente_email || 'Remetente não identificado';
}

export default function CrmEmails({ crmFetch, currentUser }) {
  const [emails, setEmails] = useState([]);
  const [status, setStatus] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);
  const [oportunidadeId, setOportunidadeId] = useState("");
  const [vinculando, setVinculando] = useState(false);
  const [ignorando, setIgnorando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('');
  const [selecionado, setSelecionado] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setCarregando(true);
    setErro('');

    try {
      const parametros = new URLSearchParams();
      if (filtro) parametros.set('classificacao', filtro);
      if (busca.trim()) parametros.set('busca', busca.trim());

      const sufixo = parametros.toString() ? `?${parametros}` : '';
      const [lista, situacao, opcoes] = await Promise.all([
        crmFetch(`/api/crm/emails${sufixo}`),
        crmFetch("/api/crm/emails/status"),
        crmFetch("/api/crm/emails/opcoes"),
      ]);

      setEmails(lista || []);
      setStatus(situacao || null);
      setOportunidades(opcoes || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  const sincronizar = async () => {
    setSincronizando(true);
    setErro('');

    try {
      await crmFetch('/api/crm/emails/sincronizar', {
        method: 'POST',
      });
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSincronizando(false);
    }
  };

  const ignorar = async () => {
    if (!selecionado) return;

    setIgnorando(true);
    setErro("");

    try {
      await crmFetch("/api/crm/emails/" + selecionado.id + "/ignorar", {
        method: "POST",
      });
      setSelecionado(null);
      setOportunidadeId("");
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setIgnorando(false);
    }
  };

  const vincular = async () => {
    if (!selecionado || !oportunidadeId) {
      setErro("Selecione uma oportunidade para vincular.");
      return;
    }

    setVinculando(true);
    setErro("");

    try {
      await crmFetch("/api/crm/emails/" + selecionado.id + "/vincular", {
        method: "POST",
        body: { oportunidade_id: Number(oportunidadeId) },
      });
      setSelecionado(null);
      setOportunidadeId("");
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setVinculando(false);
    }
  };

  const resumo = useMemo(() => ({
    total: Number(status?.total || 0),
    pendentes: Number(status?.pendentes || 0),
    vinculados: Number(status?.vinculados || 0),
    hoje: Number(status?.recebidos_hoje || 0),
  }), [status]);

  const podeSincronizar = ['admin', 'gerente'].includes(currentUser?.perfil);

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <div>
            <div className="card-title">Monitoramento de e-mails comerciais</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Caixa {status?.mailbox || 'caio.prado@flowtech.eng.br'} · Pasta CRM Flowtech
            </div>
          </div>

          {podeSincronizar && (
            <button
              className="btn btn-primary"
              onClick={sincronizar}
              disabled={sincronizando}
            >
              {sincronizando ? 'Sincronizando...' : 'Sincronizar agora'}
            </button>
          )}
        </div>

        <div style={{
          padding: '0 16px 16px',
          fontSize: 11,
          color: status?.ultimo_status === 'erro' ? '#DC2626' : 'var(--muted)',
        }}>
          Última sincronização: {dataHoraBR(status?.ultima_sincronizacao)}
          {' · '}
          Status: {status?.ultimo_status || 'aguardando'}
          {status?.ultimo_erro ? ` · ${status.ultimo_erro}` : ''}
        </div>
      </div>

      {erro && (
        <div className="card" style={{
          padding: 14,
          marginBottom: 16,
          color: '#B91C1C',
          borderColor: '#FCA5A5',
        }}>
          {erro}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 16,
      }}>
        {[
          ['Total monitorado', resumo.total],
          ['Pendentes', resumo.pendentes],
          ['Vinculados', resumo.vinculados],
          ['Recebidos hoje', resumo.hoje],
        ].map(([rotulo, valor]) => (
          <div className="card" key={rotulo} style={{ padding: 16 }}>
            <div className="kpi-label">{rotulo}</div>
            <div style={{ fontSize: 26, fontWeight: 800, marginTop: 6 }}>
              {valor}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-head" style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <input
            className="search-input"
            placeholder="Buscar assunto, remetente ou conteúdo..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') carregar();
            }}
            style={{ flex: '1 1 320px' }}
          />

          <select
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
          >
            <option value="">Todas as classificações</option>
            <option value="pendente">Pendentes</option>
            <option value="vinculado">Vinculados</option>
            <option value="ignorado">Ignorados</option>
          </select>

          <button className="btn btn-ghost" onClick={carregar}>
            Buscar
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Remetente</th>
                <th>Assunto</th>
                <th>Anexo</th>
                <th>Classificação</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>
                    Carregando e-mails...
                  </td>
                </tr>
              ) : !emails.length ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 30 }}>
                    Nenhum e-mail comercial encontrado.
                  </td>
                </tr>
              ) : emails.map(email => (
                <tr key={email.id}>
                  <td>{dataHoraBR(email.recebido_em || email.enviado_em)}</td>
                  <td>
                    <strong>{nomeRemetente(email)}</strong>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                      {email.remetente_email || '—'}
                    </div>
                  </td>
                  <td>
                    <strong>{email.assunto || '(Sem assunto)'}</strong>
                    <div style={{
                      fontSize: 10,
                      color: 'var(--muted)',
                      maxWidth: 480,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {email.resumo || 'Sem prévia disponível'}
                    </div>
                  </td>
                  <td>{email.tem_anexos ? 'Sim' : 'Não'}</td>
                  <td>{email.classificacao}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setSelecionado(email);
                        setOportunidadeId(email.oportunidade_id || "");
                      }}
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selecionado && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 720, width: '94%' }}>
            <div className="modal-head" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
            }}>
              <span className="modal-title">
                {selecionado.assunto || '(Sem assunto)'}
              </span>
              <button
                className="modal-close"
                onClick={() => setSelecionado(null)}
                style={{ border: 0, background: 'transparent', fontSize: 24 }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <div className="kpi-label">Remetente</div>
                  <div>{nomeRemetente(selecionado)}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {selecionado.remetente_email || '—'}
                  </div>
                </div>
                <div>
                  <div className="kpi-label">Recebido em</div>
                  <div>{dataHoraBR(selecionado.recebido_em)}</div>
                </div>
                <div>
                  <div className="kpi-label">Prévia da mensagem</div>
                  <div style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    padding: 14,
                    background: 'var(--surface2)',
                    borderRadius: 10,
                  }}>
                    {selecionado.resumo || 'Sem prévia disponível.'}
                  </div>
                </div>
              </div>
                <div style={{ marginTop: 16 }}>
                  <div className="kpi-label" style={{ marginBottom: 6 }}>
                    Vincular à oportunidade
                  </div>
                  <select
                    value={oportunidadeId}
                    onChange={e => setOportunidadeId(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="">Selecione uma oportunidade...</option>
                    {oportunidades.map(oportunidade => (
                      <option key={oportunidade.id} value={oportunidade.id}>
                        {(oportunidade.nome_fantasia || oportunidade.razao_social) +
                          " — " + oportunidade.titulo + " (#" + oportunidade.id + ")"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => setSelecionado(null)}
              >
                Fechar
              </button>
              {selecionado.classificacao === "pendente" && (
                <button
                  className="btn btn-ghost"
                  onClick={ignorar}
                  disabled={ignorando || vinculando}
                >
                  {ignorando ? "Ignorando..." : "Ignorar"}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={vincular}
                disabled={vinculando || !oportunidadeId}
              >
                {vinculando ? "Vinculando..." : "Vincular oportunidade"}
              </button>
              {selecionado.web_link && (
                <a
                  className="btn btn-primary"
                  href={selecionado.web_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir no Outlook
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
